import { useInfiniteQuery } from '@tanstack/react-query';
import { getRecommendedBooksForUser, type SearchBooksResponse } from '../api/books';
import { useMemo } from 'react';

interface UseRecommendedBooksInfiniteOptions {
  enabled?: boolean;
  limit?: number;
}

export const useRecommendedBooksInfinite = (options: UseRecommendedBooksInfiniteOptions = {}) => {
  const { enabled = true, limit = 10 } = options;

  const query = useInfiniteQuery({
    queryKey: ['recommendedBooksInfinite', limit],
    queryFn: async ({ pageParam = 1 }) => {
      return getRecommendedBooksForUser({ page: pageParam, limit });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination?.has_next) {
        return (lastPage.pagination.page || 1) + 1;
      }
      return undefined;
    },
    enabled,
    staleTime: 60_000,
  });

  const books = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.books || []);
  }, [query.data?.pages]);

  const totalCount = query.data?.pages[0]?.pagination?.total_count ?? 0;

  return {
    books,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isError: query.isError,
    error: query.error,
  };
};
